import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import { useEffect, useState } from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import dayjs, { Dayjs } from "dayjs";
import TimeSlots from "../../components/slots";
import { useLocation, useNavigate } from "react-router-dom";
import socket from "../../socket";
import { toast } from "react-toastify";
import StatusLegend from "../../components/slots/StatusLegend";
import bydefaultpic from "../../assets/SVG/PlaceholderImage/placeholder.svg";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
const statusData = [
  {
    label: "Available",
    color: "rgba(5, 95, 252, 1)",
  },
  {
    label: "Booked",
    color: "rgba(201, 201, 201, 1)",
  },
  {
    label: "Selected",
    color: "rgba(81, 184, 0, 1)",
  },
  {
    label: "Expired",
    color: "rgba(255, 99, 71, 1)",
  },
];

const extractTimeRange = (inputString: string) => {
  const parts = inputString.split("__");
  const timeRange = parts[2];
  const [from, to] = timeRange.split(" - ");
  return {
    from: from.trim(),
    to: to.trim(),
  };
};

const PatientAppointment = () => {
  const [doctors, setDoctors] = useState<any[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [appointmentDate, setAppointmentDate] = useState<Dayjs | null>(dayjs());
  const [serviceFee, setServiceFee] = useState<number | null>(null);
  const [payableFee, setPayableFee] = useState<number | null>(null);
  const [discount, setDiscount] = useState<number | string>(0);
  const [feeStatus, setFeeStatus] = useState<string>("");
  const theme = useTheme();
  const isxsup = useMediaQuery(theme.breakpoints.up("sm"));
  const [timeRange, setTimeRange] = useState({ from: "", to: "" });
  const [selectedSlotID, setSelectedSlotID] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const patientId = location.state.patientId;
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(
    patientId?.doctor?._id
  );
  const [formErrors, setFormErrors] = useState({
    doctor: "",
    service: "",
    date: "",
    slot: "",
    general: "",
    discount: "",
    status: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const statusOptions = [
    { name: "Paid" },
    { name: "Unpaid" },
    { name: "Insurance" },
  ];

  const numberRegex = /^[0-9]*$/;

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/getAllDoctors`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const fetchedDoc = response.data?.data || [];
      const uniqueDoc = Array.from(
        new Map(fetchedDoc.map((item: any) => [item.fullName, item])).values()
      );
      setDoctors(uniqueDoc);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (patientId?.doctor) {
      const FilterDoctorObject = doctors?.find(
        (item) => item._id === patientId?.doctor._id
      );
      setSelectedDoctor(FilterDoctorObject);
      setSelectedDoctorId(FilterDoctorObject ? FilterDoctorObject._id : null);
    } else {
      // Load last selected doctor from localStorage if no doctor in patientId
      const lastDoctorId = localStorage.getItem('lastSelectedDoctorSlot');
      if (lastDoctorId && doctors.length > 0) {
        const savedDoctor = doctors.find((doc) => doc._id === lastDoctorId);
        if (savedDoctor) {
          setSelectedDoctor(savedDoctor);
          setSelectedDoctorId(savedDoctor._id);
        }
      }
    }
  }, [doctors, patientId]);

  useEffect(() => {
    if (selectedSlotID) {
      const extractedTime = extractTimeRange(selectedSlotID);
      if (extractedTime) {
        setTimeRange(extractedTime);
      }
    }
  }, [selectedSlotID]);

  const handleDoctorChange = (event: SelectChangeEvent<any>) => {
    const doctor = event.target.value;
    setSelectedDoctor(doctor);
    setSelectedDoctorId(doctor ? doctor._id : null);
    setSelectedService(null);
    setServiceFee(null);
    setPayableFee(null);
    setDiscount(0);
    setFeeStatus("");
    setFormErrors((prev) => ({ ...prev, doctor: "", service: "", status: "" }));
  };

  const handleServiceChange = (event: SelectChangeEvent<any>) => {
    const service = event.target.value;
    setSelectedService(service);
    setServiceFee(service.fee);
    setPayableFee(service.fee);
    setDiscount(0);
    setFormErrors((prev) => ({ ...prev, service: "" }));
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    setAppointmentDate(newValue);
    setFormErrors((prev) => ({ ...prev, date: "" }));
  };

  const handleDiscountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const discountValue = event.target.value;
    if (!numberRegex.test(discountValue) && discountValue !== "") {
      setFormErrors((prev) => ({
        ...prev,
        discount: "Only numbers are allowed",
      }));
      return;
    }

    let newDiscount: number | string = discountValue
      ? parseInt(discountValue)
      : 0;

    if (newDiscount < 0 || (serviceFee && newDiscount > serviceFee)) {
      newDiscount = 0;
      setFormErrors((prev) => ({
        ...prev,
        discount: `Discount must be less than ${serviceFee}`,
      }));
    } else {
      setFormErrors((prev) => ({ ...prev, discount: "" }));
    }

    setDiscount(newDiscount);
    setPayableFee(serviceFee ? serviceFee - newDiscount : null);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const status = event.target.value;
    setFeeStatus(status);
    if (status.toLowerCase() === "insurance") {
      setDiscount(0);
      setPayableFee(serviceFee);
    }
    setFormErrors((prev) => ({ ...prev, status: "" }));
  };

  const handleSubmit = async () => {
    setFormErrors({
      doctor: "",
      service: "",
      date: "",
      slot: "",
      general: "",
      discount: "",
      status: "",
    });

    let hasError = false;

    if (!selectedDoctorId) {
      setFormErrors((prev) => ({ ...prev, doctor: "Please select a doctor." }));
      hasError = true;
    }
    if (!selectedService) {
      setFormErrors((prev) => ({
        ...prev,
        service: "Please select a service.",
      }));
      hasError = true;
    }
    if (!appointmentDate) {
      setFormErrors((prev) => ({
        ...prev,
        date: "Please select an appointment date.",
      }));
      hasError = true;
    }
    if (!selectedSlotID) {
      setFormErrors((prev) => ({
        ...prev,
        slot: "Please select a time slot.",
      }));
      hasError = true;
    }
    if (!feeStatus) {
      setFormErrors((prev) => ({ ...prev, status: "Please select a status." }));
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);
    const formattedAppointmentDate = appointmentDate?.format("YYYY-MM-DD");
    const appointmentData = {
      doctorId: selectedDoctorId,
      patientId: patientId?.patientId?._id
        ? patientId?.patientId?._id
        : patientId?._id,
      services: [selectedService._id],
      feeStatus: feeStatus.toLowerCase(),
      appointmentDate: formattedAppointmentDate,
      extra: {},
      discount: Number(discount),
      slotId: selectedSlotID,
      appointmentTime: timeRange,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/appointments/addAppointmentSlotWise`,
        appointmentData,
        {
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data.isSuccess) {
        navigate("/appointment-dashboard");
        let socketData = {
          projectId: localStorage.getItem("projectOP")
            ? localStorage.getItem("projectOP")!.toString()
            : "",
          module: "appointments",
          operation: "insert",
        };

        socket.on("connect", () => {
          console.log("Connected to the server");
        });
        socket.on("disconnect", () => {
          console.log("Server is disconnected!");
        });

        if (socket.connected) {
          socket.emit("himsEvents", socketData, (response: any) => {
            if (response?.status !== "success") {
              toast.error("Failed to process real-time update");
            }
          });
        } else {
          console.warn("Socket not connected, event buffered");
        }
        toast.info("Appointment created successfully");
      } else {
        toast.error(response?.data.message);
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      setFormErrors((prev) => ({
        ...prev,
        general: "Failed to create appointment. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };
  const handleManualAppointment = () => {
    // const appointmentData = {
    //     doctorName: selectedDoctor ? selectedDoctor.fullName : '',
    //     doctorId: selectedDoctorId || '',
    //     services: selectedService ? selectedService._id : '',
    //     appointmentDate: appointmentDate ? appointmentDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    //     appointmentTime: timeRange,
    //     discount: discount || 0,
    //     status: feeStatus || '',
    //     returnableAmount: 0,
    // };
    // navigate('/patient-registration', { state: { patientId, appointmentData } });
    navigate("/patient-registration", { state: { patientId } });
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          padding: { xs: "8px 12px", sm: "12px 19px" },
          borderRadius: "13px",
          marginTop: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "14px", sm: "18px" },
            fontWeight: "600",
            marginBottom: { xs: "5px", sm: "0" },
          }}
        >
          Patient Appointment
        </Typography>
        {isxsup && <StatusLegend statusData={statusData} />}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { sm: "flex-start", xs: "space-between" },
          mt: { md: 0.8, sm: 0.5, xs: 0.5 },
          mb: { md: 0.8, sm: 0.5, xs: 0.5 },
          gap: { md: 2, sm: 0, xs: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "12px", sm: "16px" }, fontWeight: "600" }}
          >
            Patient Name:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "12px", sm: "16px" }, fontWeight: "500" }}
          >
            {patientId?.patientId?.patientName
              ? patientId?.patientId?.patientName
              : patientId?.patientName}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "12px", sm: "16px" }, fontWeight: "600" }}
          >
            MRN:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "12px", sm: "16px" }, fontWeight: "500" }}
          >
            {patientId?.patientId?.mrn
              ? patientId?.patientId?.mrn
              : patientId?.mrn}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: "white",
          padding: "12px 19px",
          borderRadius: "13px",
          marginBottom: "25px",
        }}
      >
        <Grid container spacing={3}>
          <Grid item md={4} sm={12} xs={12}>
            <Box className="flex items-center justify-between">
              <div>
                <p className="text-black font-bold text-[14px]">Doctor Name</p>
                <p className="font-normal text-[14px]">
                  {patientId?.doctor?.fullName}
                </p>
                <p className="text-black font-bold text-[14px]">
                  Specialization
                </p>
                <p className="font-normal text-[14px]">
                  {selectedDoctor?.specialization
                    ? selectedDoctor?.specialization
                    : selectedDoctor?.doctorDetails?.specialization}
                </p>
              </div>
              <div>
                <img
                  src={
                    selectedDoctor?.doctorDetails?.photoUrl
                      ? selectedDoctor?.doctorDetails?.photoUrl
                      : bydefaultpic
                  }
                  alt=""
                  className="rounded-full size-16"
                />
              </div>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate("/doc_card", { state: { patientId } })}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  borderColor: "#055FFC",
                  color: "#055FFC",
                  "&:hover": {
                    borderColor: "#0347c4",
                    backgroundColor: "#f0f7ff",
                  },
                }}
              >
                Change Doctor
              </Button>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#3F3F3F",
                  fontWeight: "600",
                  py: 0.5,
                }}
              >
                Appointment Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{
                    "& fieldset": { border: "none" },
                    "& .MuiInputBase-root": {
                      height: "38px",
                      fontSize: "12px",
                      overflow: "hidden",
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: "#F5F5FB",
                    },
                    width: "100%",
                  }}
                  value={appointmentDate}
                  onChange={handleDateChange}
                  shouldDisableDate={(date) => {
                    if (!date) return false;
                    const today = dayjs();
                    const endOfWeek = today.add(7, "days");
                    return (
                      date.isBefore(today, "day") ||
                      date.isAfter(endOfWeek, "day")
                    );
                  }}
                />
              </LocalizationProvider>
              {formErrors.date && (
                <Typography color="error" sx={{ fontSize: "10px" }}>
                  {formErrors.date}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#3F3F3F",
                  fontWeight: "600",
                  py: 0.5,
                }}
              >
                Select Services
              </Typography>
              <Select
                size="small"
                fullWidth
                value={selectedService ? selectedService : ""}
                onChange={handleServiceChange}
                displayEmpty
                disabled={!selectedDoctor}
                sx={{
                  borderRadius: "12px",
                  background: "#F5F5FB",
                  fontSize: "14px",
                  color: "gray",
                  height: "38px",
                  boxShadow: "none",
                  "& fieldset": { border: "none" },
                }}
              >
                <MenuItem value="">Select Service</MenuItem>
                {selectedDoctor &&
                  selectedDoctor.services &&
                  selectedDoctor.services.map((service: any) => (
                    <MenuItem key={service._id} value={service}>
                      {service.serviceName}
                    </MenuItem>
                  ))}
              </Select>
              {formErrors.service && (
                <Typography color="error" sx={{ fontSize: "10px" }}>
                  {formErrors.service}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box sx={{ width: "50%" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#3F3F3F",
                    fontWeight: "600",
                    py: 0.5,
                  }}
                >
                  Total Fee (Rs)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={serviceFee !== null ? serviceFee : ""}
                  InputProps={{
                    readOnly: true,
                  }}
                  className="search-field"
                />
              </Box>
              <Box sx={{ width: "50%" }}>
                <FormHelperText
                  sx={{
                    fontSize: "14px",
                    color: "#3F3F3F",
                    fontWeight: "600",
                    py: 0.5,
                  }}
                >
                  Discount (Rs)
                </FormHelperText>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  value={discount}
                  onChange={handleDiscountChange}
                  disabled={feeStatus.toLowerCase() === "insurance"}
                  className="search-field"
                  inputProps={{ pattern: "[0-9]*" }}
                />
                {formErrors.discount && (
                  <Typography color="error" sx={{ fontSize: "10px" }}>
                    {formErrors.discount}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box sx={{ width: "50%" }}>
                <FormHelperText
                  sx={{
                    fontSize: "14px",
                    color: "#3F3F3F",
                    fontWeight: "600",
                    py: 0.5,
                  }}
                >
                  Payable Fee (Rs)
                </FormHelperText>
                <TextField
                  fullWidth
                  size="small"
                  value={payableFee !== null ? payableFee : ""}
                  InputProps={{
                    readOnly: true,
                  }}
                  className="search-field"
                />
              </Box>
              <Box sx={{ width: "50%" }}>
                <FormHelperText
                  sx={{
                    fontSize: "14px",
                    color: "#3F3F3F",
                    fontWeight: "600",
                    py: 0.5,
                  }}
                >
                  Status
                </FormHelperText>
                <FormControl fullWidth>
                  <Select
                    size="small"
                    value={feeStatus}
                    onChange={handleStatusChange}
                    displayEmpty
                    sx={{
                      borderRadius: "12px",
                      background: "#F5F5FB",
                      fontSize: "14px",
                      height: "38px",
                      boxShadow: "none",
                      "& fieldset": { border: "none" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Status
                    </MenuItem>
                    {statusOptions.map((option) => (
                      <MenuItem key={option.name} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.status && (
                    <Typography color="error" sx={{ fontSize: "10px" }}>
                      {formErrors.status}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
          </Grid>
          <Grid item md={8} sm={12} xs={12}>
            <TimeSlots
              appointmentDate={
                appointmentDate ? appointmentDate.format("YYYY-MM-DD") : ""
              }
              id={selectedDoctorId || ""}
              setSelectedSlotID={setSelectedSlotID}
            />
            {formErrors.slot && (
              <Typography color="error" sx={{ fontSize: "10px" }}>
                {formErrors.slot}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button variant="contained" onClick={handleManualAppointment}>
              Shift to Manual Appointment{" "}
              <SubdirectoryArrowRightIcon
                style={{ transform: "rotate(-90deg)" }}
              />
            </Button>
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "Done"
            )}
          </Button>
        </Box>
        {formErrors.general && (
          <Typography color="error" sx={{ mt: 2, fontSize: "10px" }}>
            {formErrors.general}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default PatientAppointment;
